import { useMemo } from 'react';
import { AccountTab, AccountTabsText, TabsPath } from 'components/commercetools-ui/organisms/account/types/types';
import { getHeadingStyles } from 'components/commercetools-ui/organisms/utils';
import { useFeatures } from 'providers/feature-flag';

type AccountTabsProps = {
  text: AccountTabsText;
  hash: string;
  emptyPageSubTitle: {
    orderStatus: string | React.ReactNode;
    addressBook: string | React.ReactNode;
    paymentMethods: string | React.ReactNode;
    accountDashboard: string | React.ReactNode;
    myPhotos: string | React.ReactNode;
  };
  isDesktopSize: boolean;
};

export const useAccountTabs = ({ text, hash, emptyPageSubTitle, isDesktopSize }: AccountTabsProps) => {
  const { enableMyPhotos } = useFeatures();
  const isMyPhotosEnabled = enableMyPhotos();
  const orderStatus = text?.orderStatus;
  const addressBook = text?.addressBook;
  const paymentMethods = text?.paymentMethods;
  const accountDashboard = text?.accountDashboard;
  const myPhotos = text?.myPhotos;
  const tabs = useMemo<AccountTab[]>(() => {
    return [
      {
        name: 'Account Dashboard',
        pageTitle: accountDashboard?.[0]?.pageHeading,
        pageTitleCss: getHeadingStyles(accountDashboard?.[0]?.pageTitleCss, isDesktopSize),
        sidebarLabel: accountDashboard?.[0]?.sidebarLabel,
        href: `?hash=${TabsPath.MY_ACCOUNT}`,
        isActive: hash === TabsPath.MY_ACCOUNT,
        showSidebarSection: false,
        icon: (
          <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 7.5H14.1667V5.83333H10V7.5ZM10 5H14.1667V3.33333H10V5ZM2.5 10H9.16667V9.54167C9.16667 8.91667 8.86111 8.42014 8.25 8.05208C7.63889 7.68403 6.83333 7.5 5.83333 7.5C4.83333 7.5 4.02778 7.68403 3.41667 8.05208C2.80556 8.42014 2.5 8.91667 2.5 9.54167V10ZM7.01042 6.17708C7.33681 5.85069 7.5 5.45833 7.5 5C7.5 4.54167 7.33681 4.14931 7.01042 3.82292C6.68403 3.49653 6.29167 3.33333 5.83333 3.33333C5.375 3.33333 4.98264 3.49653 4.65625 3.82292C4.32986 4.14931 4.16667 4.54167 4.16667 5C4.16667 5.45833 4.32986 5.85069 4.65625 6.17708C4.98264 6.50347 5.375 6.66667 5.83333 6.66667C6.29167 6.66667 6.68403 6.50347 7.01042 6.17708ZM1.66667 13.3333C1.20833 13.3333 0.815972 13.1701 0.489583 12.8438C0.163194 12.5174 0 12.125 0 11.6667V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H15C15.4583 0 15.8507 0.163194 16.1771 0.489583C16.5035 0.815972 16.6667 1.20833 16.6667 1.66667V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8438C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667ZM1.66667 11.6667H15V1.66667H1.66667V11.6667Z"
              fill="currentColor"
            />
          </svg>
        ),
        emptyPageSubTitle: emptyPageSubTitle.accountDashboard,
      },
      ...(isMyPhotosEnabled
        ? [
            {
              name: 'My Photos',
              pageTitle: myPhotos?.[0]?.pageHeading,
              pageTitleCss: getHeadingStyles(myPhotos?.[0]?.pageTitleCss, isDesktopSize),
              sidebarLabel: myPhotos?.[0]?.sidebarLabel,
              href: `?hash=${TabsPath.MY_PHOTOS}`,
              isActive: hash === TabsPath.MY_PHOTOS,
              showSidebarSection: false,
              icon: (
                <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.66667 13.3333C1.20833 13.3333 0.815972 13.1701 0.489583 12.8438C0.163194 12.5174 0 12.125 0 11.6667V3.33333C0 2.875 0.163194 2.48264 0.489583 2.15625C0.815972 1.82986 1.20833 1.66667 1.66667 1.66667H5L6.66667 0H10L11.6667 1.66667H15C15.4583 1.66667 15.8507 1.82986 16.1771 2.15625C16.5035 2.48264 16.6667 2.875 16.6667 3.33333V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8438C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667ZM8.33333 10.8333C9.26389 10.8333 10.0521 10.5104 10.6979 9.86458C11.3438 9.21875 11.6667 8.43056 11.6667 7.5C11.6667 6.56944 11.3438 5.78125 10.6979 5.13542C10.0521 4.48958 9.26389 4.16667 8.33333 4.16667C7.40278 4.16667 6.61458 4.48958 5.96875 5.13542C5.32292 5.78125 5 6.56944 5 7.5C5 8.43056 5.32292 9.21875 5.96875 9.86458C6.61458 10.5104 7.40278 10.8333 8.33333 10.8333ZM8.33333 9.16667C7.86111 9.16667 7.46528 9.00694 7.14583 8.6875C6.82639 8.36806 6.66667 7.97222 6.66667 7.5C6.66667 7.02778 6.82639 6.63194 7.14583 6.3125C7.46528 5.99306 7.86111 5.83333 8.33333 5.83333C8.80556 5.83333 9.20139 5.99306 9.52083 6.3125C9.84028 6.63194 10 7.02778 10 7.5C10 7.97222 9.84028 8.36806 9.52083 8.6875C9.20139 9.00694 8.80556 9.16667 8.33333 9.16667Z"
                    fill="currentColor"
                  />
                </svg>
              ),
              emptyPageSubTitle: emptyPageSubTitle.myPhotos,
            },
          ]
        : []),
      {
        name: 'Orders Status',
        pageTitle: orderStatus?.[0]?.pageHeading,
        pageTitleCss: getHeadingStyles(orderStatus?.[0]?.pageTitleCss, isDesktopSize),
        sidebarLabel: orderStatus?.[0]?.sidebarLabel,
        href: `?hash=${TabsPath.ORDERS_STATUS}`,
        isActive: hash === TabsPath.ORDERS_STATUS,
        showSidebarSection: true,
        icon: (
          <svg width="19" height="14" viewBox="0 0 19 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.6667 13.3333C9.80556 13.3333 8.22917 12.6875 6.9375 11.3958C5.64583 10.1042 5 8.52778 5 6.66667C5 4.81944 5.64583 3.24653 6.9375 1.94792C8.22917 0.649306 9.80556 0 11.6667 0C13.5139 0 15.0868 0.649306 16.3854 1.94792C17.684 3.24653 18.3333 4.81944 18.3333 6.66667C18.3333 8.52778 17.684 10.1042 16.3854 11.3958C15.0868 12.6875 13.5139 13.3333 11.6667 13.3333ZM13.5625 9.77083L14.75 8.58333L12.5 6.33333V3.33333H10.8333V7.02083L13.5625 9.77083ZM0.833333 4.16667V2.5H4.16667V4.16667H0.833333ZM0 7.5V5.83333H4.16667V7.5H0ZM0.833333 10.8333V9.16667H4.16667V10.8333H0.833333Z"
              fill="currentColor"
            />
          </svg>
        ),
        emptyPageSubTitle: emptyPageSubTitle.orderStatus,
      },
      {
        name: 'Address Book',
        pageTitle: addressBook?.[0]?.pageHeading,
        pageTitleCss: getHeadingStyles(addressBook?.[0]?.pageTitleCss, isDesktopSize),
        sidebarLabel: addressBook?.[0]?.sidebarLabel,
        href: `?hash=${TabsPath.ADDRESS_BOOK}`,
        isActive: hash === TabsPath.ADDRESS_BOOK,
        showSidebarSection: true,
        icon: (
          <svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1.66667 13.3333C1.20833 13.3333 0.815972 13.1701 0.489583 12.8438C0.163194 12.5174 0 12.125 0 11.6667V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H15C15.4583 0 15.8507 0.163194 16.1771 0.489583C16.5035 0.815972 16.6667 1.20833 16.6667 1.66667V11.6667C16.6667 12.125 16.5035 12.5174 16.1771 12.8438C15.8507 13.1701 15.4583 13.3333 15 13.3333H1.66667ZM8.33333 7.5L15 3.33333V1.66667L8.33333 5.83333L1.66667 1.66667V3.33333L8.33333 7.5Z"
              fill="currentColor"
            />
          </svg>
        ),
        emptyPageSubTitle: emptyPageSubTitle.addressBook,
      },
      {
        name: 'Payment Methods',
        pageTitle: paymentMethods?.[0]?.pageHeading,
        pageTitleCss: getHeadingStyles(paymentMethods?.[0]?.pageTitleCss, isDesktopSize),
        sidebarLabel: paymentMethods?.[0]?.sidebarLabel,
        href: `?hash=${TabsPath.PAYMENT_METHODS}`,
        isActive: hash === TabsPath.PAYMENT_METHODS,
        showSidebarSection: true,
        icon: (
          <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.8333 9.16667H14.1667V5.83333H12.9167V7.91667H10.8333V9.16667ZM8.33333 8.33333C9.02778 8.33333 9.61806 8.09028 10.1042 7.60417C10.5903 7.11806 10.8333 6.52778 10.8333 5.83333C10.8333 5.13889 10.5903 4.54861 10.1042 4.0625C9.61806 3.57639 9.02778 3.33333 8.33333 3.33333C7.63889 3.33333 7.04861 3.57639 6.5625 4.0625C6.07639 4.54861 5.83333 5.13889 5.83333 5.83333C5.83333 6.52778 6.07639 7.11806 6.5625 7.60417C7.04861 8.09028 7.63889 8.33333 8.33333 8.33333ZM2.5 5.83333H3.75V3.75H5.83333V2.5H2.5V5.83333ZM0 11.6667V0H16.6667V11.6667H0ZM1.66667 10H15V1.66667H1.66667V10Z"
              fill="currentColor"
            />
          </svg>
        ),
        emptyPageSubTitle: emptyPageSubTitle.paymentMethods,
      },
    ];
  }, [
    hash,
    orderStatus,
    addressBook,
    paymentMethods,
    accountDashboard,
    myPhotos,
    emptyPageSubTitle,
    isDesktopSize,
    isMyPhotosEnabled,
  ]);

  return tabs;
};
